import React, { useState } from "react";
import { Briefcase } from "lucide-react";
import { Modal } from "./Modal";
import * as api from "@/src/api/sdk.gen";
import type { WorkspaceItem } from "../../types";
import { getErrorMessage } from "../../types";

export interface CreateWorkspaceModalProps {
  isOpen: boolean;
  onClose: () => void;
  slug: string;
  onSuccess: (newWorkspace: WorkspaceItem) => Promise<void>;
}

export const CreateWorkspaceModal: React.FC<CreateWorkspaceModalProps> = ({
  isOpen,
  onClose,
  slug,
  onSuccess,
}) => {
  const [newWsName, setNewWsName] = useState("");
  const [newWsDescription, setNewWsDescription] = useState("");
  const [newWsSeatLimit, setNewWsSeatLimit] = useState("");
  const [creatingWs, setCreatingWs] = useState(false);
  const [createWsError, setCreateWsError] = useState<string | null>(null);

  const resetForm = () => {
    setNewWsName("");
    setNewWsDescription("");
    setNewWsSeatLimit("");
    setCreateWsError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!slug || !newWsName.trim()) return;
    try {
      setCreatingWs(true);
      setCreateWsError(null);
      const { data, error } = await api.createWorkspaceApiV1B2bSlugWorkspacesPost({
        path: { slug },
        body: {
          name: newWsName.trim(),
          description: newWsDescription.trim() || undefined,
          seat_limit: newWsSeatLimit.trim() ? parseInt(newWsSeatLimit.trim(), 10) : undefined,
        } as any,
      });
      if (data) {
        const created = data as unknown as WorkspaceItem;
        resetForm();
        onClose();
        await onSuccess(created);
      } else {
        setCreateWsError(getErrorMessage(error, "Failed to create workspace"));
      }
    } catch (err) {
      setCreateWsError("Network error creating workspace");
    } finally {
      setCreatingWs(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Create New Workspace"
      icon={<Briefcase className="w-5 h-5 text-indigo-600" />}
      maxWidth="max-w-md"
    >
      {createWsError && (
        <div className="mb-4 p-3 bg-rose-50 border border-rose-200 text-rose-800 rounded-xl text-xs font-semibold">
          {createWsError}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4 text-xs">
        <div>
          <label className="block font-semibold text-slate-700 mb-1.5">Workspace Name *</label>
          <input
            type="text"
            required
            placeholder="e.g. Engineering, Sales Pod, Marketing"
            value={newWsName}
            onChange={(e) => setNewWsName(e.target.value)}
            className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-indigo-500"
          />
        </div>

        <div>
          <label className="block font-semibold text-slate-700 mb-1.5">Description (Optional)</label>
          <textarea
            rows={3}
            placeholder="Workspace purpose or team scope..."
            value={newWsDescription}
            onChange={(e) => setNewWsDescription(e.target.value)}
            className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-indigo-500"
          />
        </div>

        <div>
          <label className="block font-semibold text-slate-700 mb-1.5">Dedicated Seat Quota (Optional)</label>
          <input
            type="number"
            min={1}
            placeholder="e.g. 25 (leave empty for unlimited within org limit)"
            value={newWsSeatLimit}
            onChange={(e) => setNewWsSeatLimit(e.target.value)}
            className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-indigo-500"
          />
          <p className="text-[11px] text-slate-400 mt-1">
            Limits active members who can join this department workspace.
          </p>
        </div>

        <div className="pt-4 border-t border-slate-100 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={creatingWs || !newWsName.trim()}
            className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl shadow-sm transition disabled:opacity-50"
          >
            {creatingWs ? "Creating..." : "Create Workspace"}
          </button>
        </div>
      </form>
    </Modal>
  );
};
